/**
 * SourceService — attaches folders and file selections to a project, and hands
 * their directories back.
 *
 * It is the only owner of stored handles. Everything else receives a
 * DirectoryResolver function rather than reaching in here (rule R2).
 */

import type { HandleStore } from '../adapters/handle-store';
import type { PermissionGate } from '../adapters/permission-gate';
import { BrowserFsDirectory } from '../adapters/browser-fs';
import { FileListDirectory } from '../adapters/file-list-fs';
import type { FsDirectory } from '../adapters/fs-port';
import type { SourceRepository } from '../repositories';
import type { SourceWithCount } from '../types';
import type { ChangeSink, Clock } from './ports';

export type SourceAvailability = 'ready' | 'needs-permission' | 'unavailable';

export class SourceService {
  /** Handles already confirmed this session, so we do not re-check constantly. */
  private readonly live = new Map<number, FsDirectory>();

  constructor(
    private readonly sources: SourceRepository,
    private readonly handles: HandleStore,
    private readonly permissions: PermissionGate,
    private readonly sink: ChangeSink,
    private readonly clock: Clock,
  ) {}

  list(projectId: number): SourceWithCount[] {
    return this.sources.list(projectId);
  }

  /**
   * Register a folder against a project. Re-adding the same folder reuses its
   * row, so a second scan tops the project up instead of duplicating it.
   */
  async attach(
    projectId: number,
    name: string,
    directory: FsDirectory,
    handle?: FileSystemDirectoryHandle,
  ): Promise<number> {
    const sourceId = this.rowFor(projectId, name);

    this.live.set(sourceId, directory);
    if (handle) await this.handles.saveDirectory(sourceId, handle);

    this.sink.touch();
    return sourceId;
  }

  /**
   * Register a hand-picked set of files. Each selection gets its own row, since
   * "the files I picked on Tuesday" is not the same source as Wednesday's.
   */
  async attachFiles(projectId: number, name: string, directory: FileListDirectory): Promise<number> {
    const sourceId = this.rowFor(projectId, name);

    this.live.set(sourceId, directory);
    await this.handles.saveFiles(sourceId, name, directory.handles);

    this.sink.touch();
    return sourceId;
  }

  /** Registers a directory for this session only — used by demo mode. */
  attachEphemeral(sourceId: number, directory: FsDirectory): void {
    this.live.set(sourceId, directory);
  }

  private rowFor(projectId: number, name: string): number {
    const existing = this.sources.byName(projectId, name);
    return existing?.id ?? this.sources.create(projectId, name, this.clock());
  }

  /** Can we read this source right now, without prompting? */
  async availability(sourceId: number): Promise<SourceAvailability> {
    if (this.live.has(sourceId)) return 'ready';

    const stored = await this.handles.load(sourceId);
    if (!stored) return 'unavailable';

    if (stored.kind === 'directory') {
      const state = await this.permissions.check(stored.handle, 'read');
      if (state === 'granted') {
        this.live.set(sourceId, new BrowserFsDirectory(stored.handle));
        return 'ready';
      }
      return state === 'prompt' ? 'needs-permission' : 'unavailable';
    }

    // A file selection is only usable when every file in it is still readable.
    const states = await Promise.all(
      stored.files.map((file) => this.permissions.check(file, 'read')),
    );
    if (states.every((state) => state === 'granted')) {
      this.live.set(sourceId, new FileListDirectory(stored.name, stored.files));
      return 'ready';
    }
    return states.some((state) => state === 'denied') ? 'unavailable' : 'needs-permission';
  }

  /**
   * Ask the browser to restore access. Must be called from a click — browsers
   * refuse permission prompts that are not tied to a user gesture.
   *
   * A folder is one prompt. A file selection is one prompt per file, which is
   * why the UI steers large sets towards picking the folder instead.
   */
  async requestAccess(sourceId: number): Promise<boolean> {
    const stored = await this.handles.load(sourceId);
    if (!stored) return false;

    if (stored.kind === 'directory') {
      const state = await this.permissions.request(stored.handle, 'read');
      if (state !== 'granted') return false;

      this.live.set(sourceId, new BrowserFsDirectory(stored.handle));
      return true;
    }

    for (const file of stored.files) {
      // Sequential on purpose: browsers queue these dialogs, and stopping at the
      // first refusal beats marching through fifty more.
      if ((await this.permissions.request(file, 'read')) !== 'granted') return false;
    }

    this.live.set(sourceId, new FileListDirectory(stored.name, stored.files));
    return true;
  }

  /** How many separate permission prompts reconnecting this source will cost. */
  async promptCost(sourceId: number): Promise<number> {
    const stored = await this.handles.load(sourceId);
    if (!stored) return 0;
    return stored.kind === 'directory' ? 1 : stored.files.length;
  }

  /** The DirectoryResolver every other service is given. */
  resolve = async (sourceId: number): Promise<FsDirectory | null> => {
    const live = this.live.get(sourceId);
    if (live) return live;

    return (await this.availability(sourceId)) === 'ready' ? (this.live.get(sourceId) ?? null) : null;
  };

  async detach(sourceId: number): Promise<void> {
    this.live.delete(sourceId);
    await this.handles.remove(sourceId);
    this.sources.delete(sourceId);
    this.sink.touch();
  }
}
